import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { ClaudeCodeToolSummary, ReportingCoverage } from '@ace-tracker/shared';
import { chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { CLAUDE_CODE_METRIC_INFO } from '../metricInfo';
import { RepositoryBreakdownTable } from './RepositoryBreakdownTable';
import { CacheEfficiencyChart } from './CacheEfficiencyChart';
import { ChartCard } from './ChartCard';
import { ClaudeCodeDrillDownTable } from './ClaudeCodeDrillDownTable';
import { ContextWeightChart } from './ContextWeightChart';
import { EfficiencyGauge } from './EfficiencyGauge';
import { KpiCard } from './KpiCard';
import { PricingBasisNote } from './PricingBasisNote';
import { ModelCostTable } from './ModelCostTable';
import { ProjectBreakdownTable } from './ProjectBreakdownTable';
import { TokenCompositionChart } from './TokenCompositionChart';

/** The Claude Code-specific body of the dashboard — the counterpart to CopilotDashboardBody, for ClaudeCodeToolSummary. */
export function ClaudeCodeDashboardBody({
  toolData,
  developersCount,
  coverage,
  formatCurrency,
  SectionHeading,
}: {
  toolData: ClaudeCodeToolSummary;
  developersCount: number;
  coverage: ReportingCoverage;
  formatCurrency: (value: number, currency: string) => string;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  const grouped = groupClaudeCodeTimeSeries(toolData.timeSeries);

  return (
    <>
      <section>
        <SectionHeading title="Headline metrics" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <KpiCard
            label="Indicative token cost"
            value={formatCurrency(toolData.indicativeCost.totalCost, toolData.indicativeCost.currency)}
            meta={
              toolData.indicativeCost.unpricedModels.length > 0
                ? `Per-model pricing; ${toolData.indicativeCost.unpricedModels.length} model(s) at fallback rate`
                : 'Priced per model from Settings'
            }
            flag="Estimate"
            info={CLAUDE_CODE_METRIC_INFO.indicativeCost}
            hero
            accentClassName="bg-primary-blue"
          />
          <KpiCard
            label="Sessions"
            value={toolData.activity.sessions.toLocaleString()}
            meta="Distinct local Claude Code sessions in range"
            info={CLAUDE_CODE_METRIC_INFO.sessions}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Active minutes"
            value={toolData.activity.activeMinutes.toLocaleString()}
            meta="Summed per-session span, all sessions"
            info={CLAUDE_CODE_METRIC_INFO.activeMinutes}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Active developers"
            value={String(developersCount)}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices reporting`}
            info={CLAUDE_CODE_METRIC_INFO.activeDevelopers}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Tokens processed"
            value={(toolData.contextWeight.inputTokens + toolData.contextWeight.outputTokens).toLocaleString()}
            meta={`${toolData.contextWeight.ratio > 0 ? `${toolData.contextWeight.ratio}:1` : '—'} input:output ratio`}
            info={CLAUDE_CODE_METRIC_INFO.tokensProcessed}
            accentClassName="bg-info"
          />
          <KpiCard
            label="Thinking tokens"
            value={toolData.thinkingTokens.toLocaleString()}
            meta="Extended-thinking tokens, all models"
            info={CLAUDE_CODE_METRIC_INFO.thinkingTokens}
            accentClassName="bg-info"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <EfficiencyGauge
            label="Cache hit ratio"
            value={toolData.cacheEfficiency.cacheHitRatio * 100}
            valueLabel={`${Math.round(toolData.cacheEfficiency.cacheHitRatio * 100)}%`}
            meta="Share of cache-eligible tokens reused, not rewritten"
            info={CLAUDE_CODE_METRIC_INFO.cacheEfficiency}
            size="sm"
          />
          <EfficiencyGauge
            label="Reporting coverage"
            value={coverage.coveragePercent}
            valueLabel={`${coverage.coveragePercent}%`}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices, last 48h`}
            info={CLAUDE_CODE_METRIC_INFO.reportingCoverage}
            size="sm"
          />
        </div>
        <div className="mt-3">
          <PricingBasisNote
            basis={{
              source: toolData.indicativeCost.source,
              reviewedAt: toolData.indicativeCost.pricesReviewedAt,
              stale: toolData.indicativeCost.pricesStale,
              fallbackCost: toolData.indicativeCost.byModel.filter((m) => m.pricedAs === null).reduce((sum, m) => sum + m.cost, 0),
            }}
            totalCost={toolData.indicativeCost.totalCost}
            unpricedModels={toolData.indicativeCost.unpricedModels}
          />
        </div>
      </section>

      <section>
        <ModelCostTable
          cost={toolData.indicativeCost}
          formatCurrency={formatCurrency}
          inputLabel="Input"
          cacheReadLabel="Cache read"
          showCacheWrite
          info={CLAUDE_CODE_METRIC_INFO.indicativeCost}
        />
      </section>

      <section>
        <SectionHeading title="Trends & breakdown" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <ChartCard
            title="Engagement trend"
            caption="Requests and sessions per day, corrected to max() per device"
            info={CLAUDE_CODE_METRIC_INFO.engagementTrend}
            height="h-96"
            className="lg:col-span-7"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={grouped}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="date" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Legend {...chartLegendProps} />
                <Bar dataKey="requests" fill="#0058AB" name="Requests" />
                <Bar dataKey="sessions" fill="#06D6A0" name="Sessions" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Cache efficiency"
            caption="Cache-read vs. cache-creation token volume"
            info={CLAUDE_CODE_METRIC_INFO.cacheEfficiency}
            className="lg:col-span-5"
          >
            <CacheEfficiencyChart cacheEfficiency={toolData.cacheEfficiency} />
          </ChartCard>

          <ChartCard
            title="Context weight"
            caption={`Input vs output tokens${toolData.contextWeight.ratio > 0 ? ` — ${toolData.contextWeight.ratio}:1 means context, not generation, drives spend` : ''}`}
            info={CLAUDE_CODE_METRIC_INFO.contextWeight}
            className="lg:col-span-6"
          >
            <ContextWeightChart contextWeight={toolData.contextWeight} />
          </ChartCard>

          <ChartCard
            title="Usage by model"
            caption="De-duplicated assistant-turn events per model"
            info={CLAUDE_CODE_METRIC_INFO.usageByModel}
            className="lg:col-span-6"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={toolData.byModel}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="model" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Bar dataKey="events" fill="#0058AB" name="Events" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Token composition by model"
            caption="Input, output, and cache tokens stacked per model"
            info={CLAUDE_CODE_METRIC_INFO.tokenCompositionByModel}
            height="h-96"
            className="lg:col-span-12"
          >
            <TokenCompositionChart byModel={toolData.byModel} />
          </ChartCard>
        </div>
      </section>

      <section>
        <ProjectBreakdownTable byProject={toolData.byProject} info={CLAUDE_CODE_METRIC_INFO.byProject} />
      </section>

      <section>
        <ClaudeCodeDrillDownTable breakdown={toolData.modelDeveloperBreakdown} info={CLAUDE_CODE_METRIC_INFO.drillDown} />
      </section>

      <section>
        <RepositoryBreakdownTable byBranch={toolData.byBranch} info={CLAUDE_CODE_METRIC_INFO.byBranch} />
      </section>
    </>
  );
}

function groupClaudeCodeTimeSeries(timeSeries: { date: string; metricName: string; value: number }[]) {
  const byDate = new Map<string, { date: string; requests: number; sessions: number }>();
  for (const point of timeSeries) {
    const row = byDate.get(point.date) ?? { date: point.date, requests: 0, sessions: 0 };
    if (point.metricName === 'claude.code.requests') row.requests = point.value;
    if (point.metricName === 'claude.code.sessions') row.sessions = point.value;
    byDate.set(point.date, row);
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}
